<template>
  <section class="how-it-works">
    <div class="how-it-works__container">
      <!-- Section Header -->
      <div class="how-it-works__header">
        <span class="how-it-works__badge">
          <i class="pi pi-bolt"></i>
          Plataforma Completa
        </span>
        <h2 class="how-it-works__title">
          Todo lo que necesitas para <span>email marketing</span>
        </h2>
        <p class="how-it-works__description">
          Valida, crea y envía. Una sola plataforma con IA integrada para campañas que convierten.
        </p>
      </div>
      
      <!-- Steps -->
      <div class="how-it-works__steps">
        <div class="how-it-works__step" v-for="(step, index) in steps" :key="step.title">
          <div class="how-it-works__step-number">{{ index + 1 }}</div>
          <div class="how-it-works__step-icon">
            <i :class="step.icon"></i>
          </div>
          <h3 class="how-it-works__step-title">{{ step.title }}</h3>
          <p class="how-it-works__step-description">{{ step.description }}</p>
          <ul class="how-it-works__step-features">
            <li v-for="feature in step.features" :key="feature">
              <i class="pi pi-check"></i>
              {{ feature }}
            </li>
          </ul>
        </div>
        
        <!-- Connector lines -->
        <div class="how-it-works__connector how-it-works__connector--1"></div>
        <div class="how-it-works__connector how-it-works__connector--2"></div>
      </div>
      
      <!-- Bottom CTA -->
      <div class="how-it-works__cta">
        <Button 
          label="Prueba la Plataforma Gratis"
          class="p-button-lg how-it-works__cta-button"
          icon="pi pi-arrow-right"
          iconPos="right"
          @click="emit('cta-click')"
        />
        <p class="how-it-works__cta-note">
          <i class="pi pi-sparkles"></i>
          Incluye 100 créditos de IA gratis
        </p>
      </div>
    </div>
  </section>

</template>

<script setup>
import Button from 'primevue/button'

const emit = defineEmits(['cta-click'])

const steps = [
  {
    title: 'Valida tu lista',
    icon: 'pi pi-shield',
    description: 'Limpia tu base de datos en minutos. Sin rebotes, sin problemas.',
    features: [
      'Validación SMTP en tiempo real',
      'Detección de emails temporales',
      'Hasta 1 millón de emails'
    ]
  },
  {
    title: 'Crea con IA',
    icon: 'pi pi-sparkles',
    description: 'Genera templates y textos profesionales con inteligencia artificial.',
    features: [
      'Generador de templates con IA',
      'Copywriting automático',
      'Personalización dinámica'
    ]
  },
  {
    title: 'Envía campañas',
    icon: 'pi pi-send',
    description: 'Lanza campañas de email marketing con alta entregabilidad.',
    features: [
      'Editor drag & drop',
      'Segmentación avanzada',
      'Métricas en tiempo real'
    ]
  }
];
</script>

<style lang="scss" scoped>
@import '@/design-system/variables';

.how-it-works {
  padding: $tl-section-spacing 0;
  background: $tl-white;
  position: relative;
}

.how-it-works__container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 $tl-space-6;
}

// Header
.how-it-works__header {
  text-align: center;
  max-width: 600px;
  margin: 0 auto $tl-space-16;
}

.how-it-works__badge {
  display: inline-flex;
  align-items: center;
  gap: $tl-space-2;
  padding: $tl-space-2 $tl-space-4;
  background: rgba($tl-success, 0.1);
  color: $tl-success;
  border-radius: $tl-radius-full;
  font-size: $tl-font-size-sm;
  font-weight: $tl-font-weight-medium;
  margin-bottom: $tl-space-4;
}

.how-it-works__title {
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: $tl-font-weight-bold;
  color: $tl-jet;
  margin-bottom: $tl-space-4;
  line-height: 1.2;
  
  span {
    color: $tl-primary;
  }
}

.how-it-works__description {
  font-size: $tl-font-size-lg;
  color: $tl-davys-gray;
  line-height: 1.6;
}

// Steps
.how-it-works__steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $tl-space-8;
  position: relative;
  margin-bottom: $tl-space-12;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: $tl-space-12;
  }
}

.how-it-works__step {
  background: $tl-gray-50;
  border-radius: $tl-radius-xl;
  padding: $tl-space-8;
  position: relative;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  z-index: 1;
  
  &:hover {
    transform: translateY(-4px);
    border-color: $tl-primary;
    box-shadow: 0 20px 40px rgba($tl-jet, 0.1);
  }
}

.how-it-works__step-number {
  position: absolute;
  top: -$tl-space-4;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 32px;
  background: $tl-primary;
  color: $tl-white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: $tl-font-weight-bold;
  font-size: $tl-font-size-sm;
}

.how-it-works__step-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, $tl-primary, $tl-primary-600);
  border-radius: $tl-radius-lg;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto $tl-space-5;
  
  i {
    font-size: 1.75rem;
    color: $tl-white;
  }
}

.how-it-works__step-title {
  font-size: $tl-font-size-xl;
  font-weight: $tl-font-weight-bold;
  color: $tl-jet;
  margin-bottom: $tl-space-3;
}

.how-it-works__step-description {
  font-size: $tl-font-size-base;
  color: $tl-davys-gray;
  margin-bottom: $tl-space-5;
  line-height: 1.6;
}

.how-it-works__step-features {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  
  li {
    display: flex;
    align-items: center;
    gap: $tl-space-2;
    font-size: $tl-font-size-sm;
    color: $tl-davys-gray;
    padding: $tl-space-2 0;
    
    i {
      color: $tl-success;
      font-size: 0.75rem;
    }
  }
}

// Connectors
.how-it-works__connector {
  position: absolute;
  top: 80px;
  height: 2px;
  background: linear-gradient(90deg, $tl-primary, $tl-primary-300);
  z-index: 0;
  
  @media (max-width: 900px) {
    display: none;
  }
}

.how-it-works__connector--1 {
  left: calc(33.33% + 10px);
  width: calc(33.33% - 90px);
}

.how-it-works__connector--2 {
  left: calc(66.66% + 10px);
  width: calc(33.33% - 90px);
}

// CTA
.how-it-works__cta {
  text-align: center;
}

.how-it-works__cta-button {
  background: $tl-action !important;
  border-color: $tl-action !important;
  padding: $tl-space-4 $tl-space-8 !important;
  font-weight: $tl-font-weight-semibold !important;
  
  &:hover {
    background: darken($tl-action, 8%) !important;
    border-color: darken($tl-action, 8%) !important;
  }
}

.how-it-works__cta-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $tl-space-2;
  margin-top: $tl-space-4;
  font-size: $tl-font-size-sm;
  color: $tl-gray-500;
  
  i {
    color: $tl-primary;
  }
}
</style>
